/**
 * Labels of the "Cut To" box in en / fr / es, read by `./DefineCutDisplay.tsx` and
 * `components/Product/ProductDisplayDefineCut/ProductDisplayDefineCut.tsx`. The keys of
 * `cutAttributes` are the cut field names that `@ryerson/common.process-cut` → `processCut`
 * returns; every name it can return must have an entry in every language, or the display throws.
 * The define-cut modal has its own copy of these labels in
 * `components/Shared/DefineCutModal/DefineCutModalContent.ts`, which words some of them
 * differently (fr `oneSide` is "Sur le côté" there).
 *
 * The cut text (`components/Shared/ProcessingDescription/ProcessingDescription.tsx` →
 * `processingText`) does not read these labels; it writes the same fields with English labels in
 * every language. That is a known defect against the language rule in that file's header.
 */
import { LanguageContent } from "@ryerson/frontend.common";

/** One cut field's label. `value` is the field name; the display reads only `display`. */
export type DefineCutAttributeType = {
	value: string;
	display: string;
};

/** Labels for every cut field, keyed by field name. */
export type CutAttributesType = {
	cutShape: DefineCutAttributeType;
	processing: DefineCutAttributeType;
	finish: DefineCutAttributeType;
	width: DefineCutAttributeType;
	length: DefineCutAttributeType;
	oneSide: DefineCutAttributeType;
	oneSideTolerance: DefineCutAttributeType;
	tolerance: DefineCutAttributeType;
	outsideDiameter: DefineCutAttributeType;
	outsideTolerance: DefineCutAttributeType;
	insideDiameter: DefineCutAttributeType;
	insideTolerance: DefineCutAttributeType;
	longSide: DefineCutAttributeType;
	shortSide: DefineCutAttributeType;
	widthLengthTolerance: DefineCutAttributeType;
	toleranceMin: DefineCutAttributeType;
	toleranceMax: DefineCutAttributeType;
};

/**
 * "Cut To" box strings for one language. `definedCut` is read only by
 * `components/Product/ProductDisplayDefineCut/ProductDisplayDefineCutDesktop.tsx` and
 * `ProductDisplayDefineCutMobile.tsx`, which nothing imports.
 */
export interface DefineCutDisplayText {
	definedCut: string;
	cutTo: string;
	edit: string;
	removeCut: string;
	cutAttributes: CutAttributesType;
}

/** "Cut To" box strings per language. Callers fall back to `en`. */
export const DefineCutDisplayContent: LanguageContent<DefineCutDisplayText> = {
	en: {
		definedCut: "Defined Cut",
		cutTo: "Cut To",
		edit: "Edit",
		removeCut: "Remove Cut",
		cutAttributes: {
			cutShape: {
				value: "shape",
				display: "Shape*",
			},
			processing: {
				value: "processing",
				display: "Process*",
			},
			finish: {
				value: "finish",
				display: "Finish",
			},
			width: {
				value: "width",
				display: "Width*",
			},
			length: {
				value: "length",
				display: "Length*",
			},
			oneSide: {
				value: "oneSide",
				display: "Side*",
			},
			oneSideTolerance: {
				value: "oneSideTolerance",
				display: "One Side Tolerance*",
			},
			tolerance: {
				value: "tolerance",
				display: "Tolerance*",
			},
			outsideDiameter: {
				value: "outsideDiameter",
				display: "Outside Diameter*",
			},
			outsideTolerance: {
				value: "outsideTolerance",
				display: "Outside Tolerance*",
			},
			insideDiameter: {
				value: "insideDiameter",
				display: "Inside Diameter*",
			},
			insideTolerance: {
				value: "insideTolerance",
				display: "Inside Tolerance*",
			},
			longSide: {
				value: "longSide",
				display: "Long Side*",
			},
			shortSide: {
				value: "shortSide",
				display: "Short Side*",
			},
			widthLengthTolerance: {
				value: "widthLengthTolerance",
				display: "Width/Length Tolerance*",
			},
			toleranceMin: {
				value: "toleranceMin",
				display: "Tolerance Min*",
			},
			toleranceMax: {
				value: "toleranceMax",
				display: "Tolerance Max*",
			},
		},
	},
	fr: {
		definedCut: "Coupe définie",
		cutTo: "Couper à",
		edit: "Modifier",
		removeCut: "Supprimer la coupe",
		cutAttributes: {
			cutShape: {
				value: "shape",
				display: "Forme*",
			},
			processing: {
				value: "processing",
				display: "Traitement*",
			},
			finish: {
				value: "finish",
				display: "Finition",
			},
			width: {
				value: "width",
				display: "Largeur*",
			},
			length: {
				value: "length",
				display: "Longueur*",
			},
			oneSide: {
				value: "oneSide",
				display: "Côté*",
			},
			oneSideTolerance: {
				value: "oneSideTolerance",
				display: "Tolérance d'un côté*",
			},
			tolerance: {
				value: "tolerance",
				display: "Tolérance*",
			},
			outsideDiameter: {
				value: "outsideDiameter",
				display: "Diamètre extérieur*",
			},
			outsideTolerance: {
				value: "outsideTolerance",
				display: "Tolérance extérieure*",
			},
			insideDiameter: {
				value: "insideDiameter",
				display: "Diamètre intérieur*",
			},
			insideTolerance: {
				value: "insideTolerance",
				display: "Tolérance intérieure*",
			},
			longSide: {
				value: "longSide",
				display: "Côté long*",
			},
			shortSide: {
				value: "shortSide",
				display: "Côté court*",
			},
			widthLengthTolerance: {
				value: "widthLengthTolerance",
				display: "Tolérance largeur/longueur*",
			},
			toleranceMin: {
				value: "toleranceMin",
				display: "Tolérance min*",
			},
			toleranceMax: {
				value: "toleranceMax",
				display: "Tolérance maximale*",
			},
		},
	},
	es: {
		definedCut: "Corte definido",
		cutTo: "Cortar a",
		edit: "Editar",
		removeCut: "Quitar corte",
		cutAttributes: {
			cutShape: {
				value: "shape",
				display: "Forma*",
			},
			processing: {
				value: "processing",
				display: "Proceso*",
			},
			finish: {
				value: "finish",
				display: "Finicion",
			},
			width: {
				value: "width",
				display: "Ancho*",
			},
			length: {
				value: "length",
				display: "Largura*",
			},
			oneSide: {
				value: "oneSide",
				display: "Lado*",
			},
			oneSideTolerance: {
				value: "oneSideTolerance",
				display: "Tolerancia de un lado*",
			},
			tolerance: {
				value: "tolerance",
				display: "Tolerancia*",
			},
			outsideDiameter: {
				value: "outsideDiameter",
				display: "Diámetro exterior*",
			},
			outsideTolerance: {
				value: "outsideTolerance",
				display: "Tolerancia exterior*",
			},
			insideDiameter: {
				value: "insideDiameter",
				display: "Diámetro interior*",
			},
			insideTolerance: {
				value: "insideTolerance",
				display: "Tolerancia interior*",
			},
			longSide: {
				value: "longSide",
				display: "Lado largo*",
			},
			shortSide: {
				value: "shortSide",
				display: "Lado corto*",
			},
			widthLengthTolerance: {
				value: "widthLengthTolerance",
				display: "Tolerancia de ancho/largo*",
			},
			toleranceMin: {
				value: "toleranceMin",
				display: "Tolerancia mínima*",
			},
			toleranceMax: {
				value: "toleranceMax",
				display: "Tolerancia máx*",
			},
		},
	},
};
